import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Grid, TextField, IconButton, Button, Box, CircularProgress, Autocomplete, Tooltip, FormControlLabel, Checkbox } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import { getDayLabel, InvoiceDays } from '.';
import { getBankDropdown, getInvoiceConfigApi, updateInvoiceConfigApi } from '../apiService';
import { checkApiStatus } from '../../../utils';
import { globalGetService, globalPutService } from '../../../utils/globalApiServices';
import "../styles/reserverate.scss"
import { PermissionWrapper } from '../../../wrappers';
import { trackActivity } from '../../../utils/mixpanel';
import { regexConstants } from '../../../constants/regEx';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { backendDateFormat, fieldDateFormat } from '../../../constants';
import { useSelector } from 'react-redux';
import LessMoreTextFieldComp from '../../../shared_elements/components/LessMoreTextFieldComp';
import AutocompleteCreatable from '../../../shared_elements/components/AutocompleteCreatable';
import BackToLeaseList from '../../elements/BackToLeaseList';

const InvoiceConfiguration = ({ tabIndex }) => {
    const { leaseSlug } = useParams();
    const params = { leaseSlug: leaseSlug };
    const { enqueueSnackbar } = useSnackbar();
    const [editMode, setEditMode] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [invoiceConfig, setInvoiceConfig] = useState({});
    const [bank, setBank] = useState([])
    const [error, setError] = useState({});
    const { leaseBasicDetails } = useSelector((state) => state.leases)

    useEffect(() => {
        getInvoiceConfig();
        trackActivity('Page Viewed', { page_title: 'Reserve Rate Invoice Configuration' })
    }, []);

    useEffect(() => {
        const id = leaseBasicDetails?.lessor?.id;
        if (!id) return;

        getBankDetails();
    }, [leaseBasicDetails?.lessor?.id]);

    const getInvoiceConfig = () => {
        setLoading(true);
        globalGetService(getInvoiceConfigApi(params), {}).then(response => {
            setLoading(false);
            if (checkApiStatus(response)) {
                setInvoiceConfig({...response?.data?.data,email_cc:response?.data?.data?.email_cc?response?.data?.data?.email_cc.split(','):[]})
            }
        });
    }

    const getBankDetails = () => {
        globalGetService(getBankDropdown(leaseBasicDetails?.lessor?.id))
            .then(response => {
                if (checkApiStatus(response)) {
                    setBank(response.data.data)
                }
            })
    }
    const onFieldChange = (key, value) => {
        if (Array.isArray(value)) {
            const unique = [];

            value.forEach((item) => {
                const email = item?.label || item;
                if (!unique.includes(email) && regexConstants.email.test(email)) {
                    unique.push(email);
                }
            });

            const formatted = unique.map((e) => (e));
            setInvoiceConfig({ ...invoiceConfig, [key]: formatted });
        }
        else {
            setInvoiceConfig((prev) => ({ ...prev, [key]: value }));
        }
    };

    const onRestErrorKey = (key) => {
        setError({ ...error, [key]: '' })
    }

    const validateDate = (value, fieldLabel) => {
        if (!value) return "";
        const leaseStartDate = moment(leaseBasicDetails?.start_date);
        const leaseEndDate = moment(leaseBasicDetails?.end_date);
        const date = moment(value);

        if (!date.isValid()) {
            return `Please enter valid ${fieldLabel}`;
        }
        if (!leaseStartDate?.isValid() || !leaseEndDate?.isValid()) {
            return "";
        }
        return date.isBetween(leaseStartDate, leaseEndDate, "day", "[]")
            ? ""
            : `${fieldLabel} must be within the lease period`;
    };


    const handleSave = () => {
        let validationInputs = {}
        validationInputs = {
            ...validationInputs,
            invoice_date: validateDate(invoiceConfig?.invoice_date,"Invoice Date"),
            reserve_remarks_date:validateDate(invoiceConfig?.reserve_remarks_date,"Remarks Date"),
        }

        const payload = {
            invoice_generation_day: invoiceConfig?.invoice_generation_day ? invoiceConfig?.invoice_generation_day : null,
            invoice_due_month: invoiceConfig?.invoice_due_month ? invoiceConfig?.invoice_due_month : null,
            invoice_due_day: invoiceConfig?.invoice_due_day ? invoiceConfig?.invoice_due_day : null,
            invoice_bank: invoiceConfig?.invoice_bank ? invoiceConfig?.invoice_bank?.id : null,
            reserve_remarks: invoiceConfig?.reserve_remarks ? invoiceConfig?.reserve_remarks : null,
            reserve_reference_number: invoiceConfig?.reserve_reference_number ? invoiceConfig?.reserve_reference_number : null,
            reserve_qis_number: invoiceConfig?.reserve_qis_number ? invoiceConfig?.reserve_qis_number : null,
            reserve_sales_order_number: invoiceConfig?.reserve_sales_order_number ? invoiceConfig?.reserve_sales_order_number : null,
            invoice_date: invoiceConfig?.invoice_date ? moment(invoiceConfig?.invoice_date).format(backendDateFormat) : null,
            mr_payment_obligation: invoiceConfig?.mr_payment_obligation ? invoiceConfig?.mr_payment_obligation : null,
            pledged: invoiceConfig?.pledged ? invoiceConfig?.pledged : null,
            email_cc: invoiceConfig?.email_cc ? invoiceConfig?.email_cc : null,
            correction_for_non_business_days: invoiceConfig?.correction_for_non_business_days ? invoiceConfig?.correction_for_non_business_days : null,
            interest_accrual: invoiceConfig?.interest_accrual ? invoiceConfig?.interest_accrual : null,
            reserve_remarks_date: invoiceConfig?.reserve_remarks_date ? moment(invoiceConfig?.reserve_remarks_date).format(backendDateFormat) : null,
            invoice_reporting_frequency: invoiceConfig?.invoice_reporting_frequency ? invoiceConfig?.invoice_reporting_frequency : null,
            reserve_invoice_is_due_day_last_day_month: invoiceConfig?.reserve_invoice_is_due_day_last_day_month ? invoiceConfig?.reserve_invoice_is_due_day_last_day_month : false,
        };
        if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
            if (invoiceConfig?.invoice_due_month === "Same Month" && Number(invoiceConfig?.invoice_due_day) < Number(invoiceConfig?.invoice_generation_day)) {
                return enqueueSnackbar(`Invoice Due Day (${invoiceConfig?.invoice_due_day}) should not be less than Invoice Generation Day (${invoiceConfig?.invoice_generation_day}) for Same Month.`, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } });
            } else {
                setLoading(true);
                globalPutService(updateInvoiceConfigApi(params), payload)
                    .then(async (response) => {
                        if (checkApiStatus(response)) {
                            setEditMode(false)
                            getInvoiceConfig();
                            setLoading(false);
                            enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
                            trackActivity('Item Edited', { page_title: `Edit Reserve Rate Invoice Configuration`, success_msg: response?.data?.message, item_slug: leaseSlug, payload: payload, response: response?.data })
                        } else {
                            setLoading(false);
                            enqueueSnackbar(response.data.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                            trackActivity('Item Edited', { page_title: `Edit Reserve Rate Invoice Configuration`, error_msg: response?.data?.message, item_slug: leaseSlug, payload: payload, response: response?.data })
                        }
                    });
            }
        }
        else {
            setError(validationInputs);
        }
    };
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Reserve Rate Invoice Configuration`, page_type: page_type, })
    }

    const emailValue = Array.isArray(invoiceConfig?.email_cc) ? invoiceConfig.email_cc.join(", ") : invoiceConfig?.email_cc || "";
    
    return (
        <Grid className="reserve-invoice-config-section">
            {isLoading ? (
                <Box display="flex" justifyContent="center" py={16}>
                    <CircularProgress size={26} />
                </Box>
            ) : (
                <>
                    <div style={{ marginTop: editMode ? '20px' : '10px !important' }}>
                        <Grid container spacing={1} alignItems='flex-end' style={{ display: "flex", justifyContent: "end" }}>
                            <Grid item size={{ md: 12, xs: 12 }}     sx={{ minHeight: '40px' }}>
                                <ul className='list-inline lease-details-right-items' style={{ float: 'right', padding: '0px' }}>
                                    <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "reserve_rates", operationType: ['U'] }}>
                                        {!editMode && !isLoading && (
                                                <Tooltip title='Edit'>
                                                    <IconButton size="small" onClick={() => { setEditMode(true); handleItemClicked('EditIcon') }} color='primary'>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                        )}
                                    </PermissionWrapper>
                                </ul>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item size={{ md: 3, xs: 12 }}>
                                <Autocomplete
                                    disablePortal
                                    options={InvoiceDays}
                                    getOptionLabel={(option) => getDayLabel(option)}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            {String(option)}
                                        </li>
                                    )}
                                    slotProps={{ listbox: { sx: { maxHeight: 155 } } }}
                                    value={invoiceConfig.invoice_generation_day || null}
                                    onChange={(e, value) => onFieldChange("invoice_generation_day", value)}
                                    disabled={!editMode}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Invoice Generation Day"
                                            margin="normal"
                                            size="small"
                                            slotProps={{ inputLabel: { shrink: true } }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="invoice_due_day"
                                    value={invoiceConfig.invoice_due_day}
                                    onChange={(e, value) => regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("invoice_due_day", e.target.value.trimStart()) : e.preventDefault()}
                                    label="Invoice Due Day"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 2 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid item size={{ md: 3, xs: 12 }}>
                                <Autocomplete
                                    disablePortal
                                    options={bank}
                                    getOptionLabel={(option) => option.name || ""}
                                    value={(bank || []).find((opt) => opt?.id === invoiceConfig?.invoice_bank?.id) || null}
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    onChange={(e, value) => onFieldChange("invoice_bank", value || '')}
                                    disabled={!editMode}
                                    slotProps={{ paper: { sx: { maxHeight: 155 } } }}
                                    renderInput={(params) => {
                                        const selectedLabel = params?.inputProps?.value || "";
                                        return (
                                            <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow ><TextField  {...params} label="Invoice Bank" margin="normal" size="small" slotProps={{ inputLabel: { shrink: true } }} /></Tooltip>)
                                    }}
                                />
                                </Grid>
                                {!editMode ?
                                    <Grid size={{ md: 3, xs: 12 }}>
                                        <Tooltip title={emailValue?.length > 30 ? emailValue : ""} arrow>
                                            <TextField
                                                id="email_cc"
                                                value={Array.isArray(invoiceConfig?.email_cc) ? invoiceConfig?.email_cc.join(', ') : invoiceConfig?.email_cc || ''}
                                                label="Email CC"
                                                variant="outlined"
                                                size="small"
                                                margin="normal"
                                                slotProps={{ inputLabel: { shrink: true } }}
                                                fullWidth
                                                disabled={true}
                                            />
                                        </Tooltip>
                                    </Grid>
                                    : <Grid size={{ md: 3, xs: 12 }}>
                                    <AutocompleteCreatable
                                        options={[]}
                                        label='Email CC'
                                        value={Array.isArray(invoiceConfig?.email_cc)?invoiceConfig?.email_cc: []}
                                        paramsKey='email_cc'
                                        onFieldChange={(e,paramsKey, newValue) => onFieldChange('email_cc', newValue)}
                                        multiple={true}
                                        noOptionsText={'No options, please type to create new'}
                                        placeholder={!editMode ? '' : 'Enter Email(s)'}
                                        error={error?.email_cc}
                                        helperText={error?.email_cc || ''}
                                        isDisabled={!editMode}
                                        useLimitTags={1}
                                        
                                    />
                            </Grid>}
                            <Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="correction_for_non_business_days"
                                    value={invoiceConfig?.correction_for_non_business_days}
                                    onChange={(e, value) => onFieldChange("correction_for_non_business_days", e.target.value.trimStart())}
                                    label="Correction For Non Business Days"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="invoice_reporting_frequency"
                                    value={invoiceConfig?.invoice_reporting_frequency}
                                    onChange={(e, value) => onFieldChange("invoice_reporting_frequency", e.target.value.trimStart())}
                                    label="Invoice/Reporting Frequency"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Invoice Date"
                                        value={invoiceConfig?.invoice_date ? moment(invoiceConfig.invoice_date) : null}
                                        onChange={(value) => {
                                            onFieldChange("invoice_date", value);
                                                onRestErrorKey('invoice_date')
                                        }}
                                        slotProps={{
                                            textField: {
                                                onFocus: () => onRestErrorKey('invoice_date'),
                                                InputLabelProps: { shrink: true },
                                                size: "small",
                                                margin: "normal",
                                                helperText: error.invoice_date || '',
                                                error: error.invoice_date || false
                                            },
                                        }}
                                        sx={{ width: "100%" }}
                                        format={fieldDateFormat}
                                        disabled={!editMode}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="mr_payment_obligation"
                                    value={invoiceConfig?.mr_payment_obligation}
                                    onChange={(e, value) => onFieldChange("mr_payment_obligation", e.target.value.trimStart())}
                                    label="MR Payment Obligation"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="pledged"
                                    value={invoiceConfig?.pledged}
                                    onChange={(e, value) => onFieldChange("pledged", e.target.value.trimStart())}
                                    label="Pledged"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="interest_accrual"
                                    value={invoiceConfig?.interest_accrual}
                                    onChange={(e, value) => onFieldChange("interest_accrual", e.target.value.trimStart())}
                                    label="Interest Accrual"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="reserve_reference_number"
                                    value={invoiceConfig?.reserve_reference_number}
                                    onChange={(e, value) => onFieldChange("reserve_reference_number", e.target.value.trimStart())}
                                    label="Reference Number"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="reserve_qis_number"
                                    value={invoiceConfig?.reserve_qis_number}
                                    onChange={(e, value) => onFieldChange("reserve_qis_number", e.target.value.trimStart())}
                                    label="QIS Number"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid><Grid size={{ md: 3, xs: 12 }}>
                                <TextField
                                    id="reserve_sales_order_number"
                                    value={invoiceConfig?.reserve_sales_order_number}
                                    onChange={(e, value) => onFieldChange("reserve_sales_order_number", e.target.value.trimStart())}
                                    label="Sales Order Number"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 50 } }}
                                    fullWidth
                                    disabled={!editMode}
                                />
                            </Grid>
                            <Grid size={{ md: 3, xs: 12 }}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Remarks Date"
                                        value={invoiceConfig?.reserve_remarks_date ? moment(invoiceConfig.reserve_remarks_date) : null}
                                        onChange={(value) => {
                                            onFieldChange("reserve_remarks_date", value);
                                                onRestErrorKey("reserve_remarks_date");
                                        }}
                                        slotProps={{
                                            textField: {
                                                onFocus: () => onRestErrorKey('reserve_remarks_date'),
                                                InputLabelProps: { shrink: true },
                                                size: "small",
                                                margin: "normal",
                                                helperText: error.reserve_remarks_date || '',
                                                error: error.reserve_remarks_date || false
                                            },
                                        }}
                                        sx={{ width: "100%" }}
                                        format={fieldDateFormat}
                                        disabled={!editMode}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid size={{ md: 4, xs: 12 }}>
                                <FormControlLabel
                                    control={<Checkbox disableRipple size="small" color="primary" disabled={!editMode} onChange={(e) => onFieldChange("reserve_invoice_is_due_day_last_day_month", e.target.checked)} checked={!!invoiceConfig?.reserve_invoice_is_due_day_last_day_month} />}
                                    label="Invoice Due Day falls at last day of the Billing Month"
                                />
                            </Grid>
                            { editMode ?
                            <Grid item size={{ md: 12, xs: 12 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={10}
                                    size="small"
                                    label="Remarks"
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    value={invoiceConfig.reserve_remarks}
                                    onChange={(e, value) => onFieldChange('reserve_remarks', e.target.value.trimStart())}
                                    disabled={!editMode}
                                />
                            </Grid>
                             :<Grid item size={{ md: 12, xs: 12 }}>
                                <LessMoreTextFieldComp label='Remarks' value={invoiceConfig.reserve_remarks}/>
                             </Grid>}
                        </Grid>
                       
                    </div>

                    {editMode && (
                        <Box className="invoice-config-actions-bar">
                            <Button className='cancel-btn' onClick={() => { getInvoiceConfig(); setEditMode(false); handleItemClicked('Cancel Button') }} variant="outlined" size="small" disabled={isLoading}>
                                Cancel
                            </Button>
                            <Button className='btn-primary' onClick={() => { handleItemClicked('Save Button'); handleSave(); }} variant="contained" size="small">
                                {isLoading ? <CircularProgress color='inherit' size={24} /> : 'Save'}
                            </Button>
                        </Box>
                    )}
                </>
            )}
        </Grid>
    );
};

export default InvoiceConfiguration;
